import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Camera, User, X, Upload, Check } from "lucide-react";
import { avatarSrc, compressImage, describeUploadError, uploadAvatar } from "@/lib/avatar";

export const PROFILE_OPEN_EVENT = "ism:open-profile";
export const PROFILE_UPDATED_EVENT = "ism:profile-updated";

const ProfileCompletionPrompt = ({ userId, onComplete, onDismiss }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [name, setName] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [preview, setPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const checkProfile = useCallback(async () => {
    if (!userId || userId === "new") return;
    try {
      const response = await axios.get(`${API}/team/${userId}/me`);
      setProfile(response.data);
      setName(response.data.name || "");
      setPhotoUrl(response.data.photoUrl || "");
      // Server decides: incomplete AND not snoozed (7 days) AND not disabled
      if (response.data.showProfilePrompt) {
        setTimeout(() => setIsOpen(true), 2000);
      }
    } catch (error) {
      console.error("Error checking profile:", error);
    }
  }, [userId]);

  useEffect(() => {
    checkProfile();
  }, [checkProfile]);

  // Header avatar (or anything else) can open the editor on demand
  useEffect(() => {
    const open = () => { setUploadError(""); setIsOpen(true); };
    window.addEventListener(PROFILE_OPEN_EVENT, open);
    return () => window.removeEventListener(PROFILE_OPEN_EVENT, open);
  }, []);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadError("");
    setUploading(true);
    setProgress(0);
    try {
      const blob = await compressImage(file);
      const localUrl = URL.createObjectURL(blob);
      setPreview(localUrl);
      const result = await uploadAvatar(userId, blob, setProgress);
      setPhotoUrl(result.photoUrl);
      toast.success(`Photo uploaded (${Math.round(result.size / 1024)}KB)`);
    } catch (error) {
      console.error("Avatar upload error:", error);
      const msg = describeUploadError(error);
      setUploadError(msg);
      setPreview("");
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/team/${userId}/me`, {
        name: name.trim(),
        photoUrl: photoUrl || null,
        avatar: photoUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(name.trim())}`,
      });
      setProfile(r.data);
      toast.success(r.data.profileComplete ? "Profile complete!" : "Profile saved — add a photo any time from the top bar.");
      window.dispatchEvent(new CustomEvent(PROFILE_UPDATED_EVENT, { detail: r.data }));
      setIsOpen(false);
      if (onComplete) onComplete(r.data);
    } catch (error) {
      console.error("Save error:", error);
      toast.error(describeUploadError(error));
    } finally {
      setSaving(false);
    }
  };

  const closeWith = async (action) => {
    setIsOpen(false);
    if (profile?.profileComplete) return; // just an edit session, nothing to snooze
    try {
      await axios.put(`${API}/team/${userId}/me/prompt`, { action });
      if (action === "never") toast.info("Okay — we won't ask again. Edit your profile from the avatar in the top bar.");
    } catch (error) {
      console.error("Prompt preference error:", error);
    }
    if (onDismiss) onDismiss(action);
  };

  if (!isOpen) return null;
  const shown = preview || avatarSrc(photoUrl);
  const complete = !!profile?.profileComplete;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }} data-testid="profile-prompt">
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="w-full max-w-md mx-4">
          <div className="rounded-2xl overflow-hidden" style={{ backgroundColor: '#111111', border: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="p-6 border-b border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-ism-teal/20"><User className="h-5 w-5 text-ism-teal" /></div>
                  <div>
                    <h2 className="text-lg font-semibold text-white">{complete ? "Your Profile" : "Complete Your Profile"}</h2>
                    <p className="text-sm text-gray-500">{complete ? "Update your photo or name" : "Add your photo and name"}</p>
                  </div>
                </div>
                <button onClick={() => closeWith("snooze")} className="p-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="profile-prompt-close-btn">
                  <X className="h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              <div className="flex flex-col items-center">
                <div onClick={() => !uploading && fileInputRef.current?.click()} className="relative w-24 h-24 rounded-full overflow-hidden cursor-pointer group"
                  style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '2px dashed rgba(255,255,255,0.2)' }} data-testid="profile-photo-dropzone">
                  {shown ? (
                    <img src={shown} alt="Profile" className="w-full h-full object-cover" data-testid="profile-photo-preview" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center"><Camera className="h-8 w-8 text-gray-500" /></div>
                  )}
                  <div className={`absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity ${uploading ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
                    {uploading ? (
                      <span className="text-xs text-white font-medium" data-testid="profile-photo-progress">{progress < 100 ? `${progress}%` : "…"}</span>
                    ) : (
                      <Upload className="h-6 w-6 text-white" />
                    )}
                  </div>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*,.heic,.heif" onChange={handleFileSelect} className="hidden" data-testid="profile-photo-input" />
                <p className="text-xs text-gray-500 mt-2">{photoUrl ? "Click to change photo" : "Click to upload photo"} · JPG, PNG or WebP · auto-compressed</p>
                {uploadError && (
                  <p className="mt-2 text-xs text-red-300 text-center max-w-xs" data-testid="profile-photo-error">{uploadError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Your Name</label>
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter your full name"
                  className="bg-white/5 border-white/10 text-white placeholder:text-gray-500" data-testid="profile-name-input" />
              </div>

              <p className="text-xs text-gray-500 text-center">Your photo and name appear in the top bar and on the team leaderboard</p>
            </div>

            <div className="p-6 border-t border-white/10">
              <div className="flex gap-3">
                <Button onClick={() => closeWith("snooze")} variant="ghost" className="flex-1 border border-white/10 text-gray-400 hover:text-white" data-testid="profile-prompt-later-btn">
                  {complete ? "Cancel" : "Later"}
                </Button>
                <Button onClick={handleSave} disabled={saving || uploading || !name.trim()} className="flex-1 bg-ism-teal hover:bg-ism-teal/90 text-white" data-testid="profile-save-btn">
                  {saving ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : (<><Check className="h-4 w-4 mr-2" />Save Profile</>)}
                </Button>
              </div>
              {!complete && (
                <button onClick={() => closeWith("never")} className="mt-3 w-full text-center text-xs text-gray-500 hover:text-gray-300 transition-colors" data-testid="profile-prompt-never-btn">
                  Don't ask again
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ProfileCompletionPrompt;
